import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, Chip, Breadcrumbs as MuiBreadcrumbs, useTheme } from '@mui/material';
export const BREADCRUMB_STATUS = {
    COMPLETE: 'complete',
    CURRENT: 'current',
    UPCOMING: 'upcoming',
};
const CircleChip = ({ label, size = 26, color, border, backgroundColor, }) => {
    return (_jsx(Chip, { label: label, sx: {
            width: size,
            height: size,
            minWidth: size,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color,
            border,
            backgroundColor,
            marginRight: '0.8rem',
            px: 0,
            '& .MuiChip-label': {
                whiteSpace: 'normal',
                overflow: 'visible',
                textOverflow: 'clip',
                fontWeight: '500',
            },
        } }));
};
/**
 * Returns the icon and color styling for a given breadcrumb status.
 *
 * This keeps visual logic separate from the JSX for readability.
 */
const getBreadcrumbIcons = (status, index, theme) => {
    switch (status) {
        /**
         * Completed breadcrumb — shows a success check icon
         */
        case BREADCRUMB_STATUS.COMPLETE:
            return {
                icon: _jsx(CheckCircleIcon, { color: "success", sx: { height: '1.8rem', width: '1.8rem', mr: 1 } }),
            };
        /**
         * Current breadcrumb — shows a numbered chip to indicate active progress
         */
        case BREADCRUMB_STATUS.CURRENT:
            return {
                icon: (_jsx(CircleChip, { label: index + 1, color: theme.palette.common.white, backgroundColor: theme.palette.primary.main })),
            };
        /**
         * Upcoming breadcrumb — shows an empty circle
         */
        case BREADCRUMB_STATUS.UPCOMING:
        default:
            return {
                icon: (_jsx(CircleChip, { label: index + 1, color: theme.palette.primary.main, border: `1px solid ${theme.palette.primary.main}`, backgroundColor: "transparent" })),
            };
    }
};
/**
 * Hierarchical breadcrumb navigation component.
 *
 * ## Features
 * - Displays progress across multiple steps
 * - Supports "complete", "current", and "upcoming" icon states
 * - Cleanly separates logic from presentation for maintainability
 */
export const Breadcrumbs = ({ breadcrumbs, className }) => {
    const theme = useTheme();
    const separatorEl = _jsx("span", { style: { display: 'block', width: '100%', borderBottom: '1px solid currentColor' } });
    return (_jsx(MuiBreadcrumbs, { className: className, separator: separatorEl, "aria-label": "breadcrumb", sx: {
            '& .MuiBreadcrumbs-ol': {
                width: '100%',
            },
            '& .MuiBreadcrumbs-separator': {
                flex: '1 1 0',
                minWidth: '1rem',
                margin: '0 0.5rem',
                verticalAlign: 'middle',
                color: theme.palette.text.disabled,
            },
        }, children: breadcrumbs.map((item, index) => {
            const { icon } = getBreadcrumbIcons(item.status, index, theme);
            return (_jsxs(Box, { color: "primary", sx: {
                    display: 'flex',
                    alignItems: 'center',
                    fontWeight: 'normal',
                }, children: [icon, item.label] }, item.label));
        }) }));
};
//# sourceMappingURL=data:application/json;base64,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